import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useParams } from 'react-router-dom';
import { format } from 'date-fns';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  Loader2, AlertTriangle, Users, Target, BarChart3, Award, Lock, Eye, Clock,
} from 'lucide-react';
import {
  RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis, Radar,
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip as ReTooltip, ResponsiveContainer,
  PieChart, Pie, Cell, Legend,
} from 'recharts';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const COLORS = ['#002FA7', '#34C759', '#FF9500', '#FF3B30', '#007AFF', '#AF52DE'];
const MATURITY_COLORS = { Initial: '#FF3B30', Developing: '#FF9500', Intermediate: '#007AFF', Advanced: '#34C759' };

export default function PublicAnalyticsSnapshotPage() {
  const { token } = useParams();
  const [snap, setSnap] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(`${API}/public/analytics/${token}`);
        if (!cancelled) setSnap(res.data);
      } catch (e) {
        if (!cancelled) {
          const status = e.response?.status;
          const detail = e.response?.data?.detail || 'Unable to load snapshot';
          setError({ status, detail });
        }
      } finally { if (!cancelled) setLoading(false); }
    })();
    return () => { cancelled = true; };
  }, [token]);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F9FAFB] flex items-center justify-center" data-testid="public-snapshot-loading">
        <Loader2 className="w-8 h-8 animate-spin text-[#002FA7]" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-[#F9FAFB] flex items-center justify-center p-6" data-testid="public-snapshot-error">
        <Card className="max-w-md border-[#E5E7EB]">
          <CardContent className="p-8 text-center">
            <AlertTriangle className="w-12 h-12 text-[#FF9500] mx-auto mb-4" />
            <h1 className="text-xl font-bold text-[#0A0A0A] mb-2">
              {error.status === 410 ? 'Snapshot Unavailable' : 'Snapshot Not Found'}
            </h1>
            <p className="text-sm text-[#4B5563] mb-4">{error.detail}</p>
            <p className="text-[11px] text-[#9CA3AF]">Please request a fresh link from the person who shared this with you.</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  const p = snap.payload || {};
  const sectionAverages = p.section_averages || {};
  const radarData = Object.entries(sectionAverages).map(([name, value]) => ({ subject: name, score: value, fullMark: 100 }));
  const maturityData = Object.entries(p.maturity_distribution || {}).map(([name, value]) => ({ name, value }));
  const barData = Object.entries(sectionAverages).map(([name, value]) => ({ name, score: value }));

  return (
    <div className="min-h-screen bg-[#F9FAFB]" data-testid="public-snapshot-page">
      {/* Header banner */}
      <div className="bg-[#002FA7] text-white">
        <div className="max-w-6xl mx-auto px-6 py-6 flex items-center justify-between flex-wrap gap-3">
          <div>
            <p className="text-[11px] uppercase tracking-wider opacity-70 mb-1">KStreak · Read-only snapshot</p>
            <h1 className="text-2xl font-bold">Team Assessment Analytics</h1>
          </div>
          <div className="flex items-center gap-2 text-[11px] bg-white/10 px-3 py-2 rounded-md">
            <Lock className="w-3 h-3" />
            <span>Tokenized public link · No account needed</span>
          </div>
        </div>
      </div>

      {/* Meta strip */}
      <div className="border-b border-[#E5E7EB] bg-white">
        <div className="max-w-6xl mx-auto px-6 py-3 flex items-center gap-4 flex-wrap text-[11px] text-[#4B5563]" data-testid="snapshot-meta">
          <span><strong className="text-[#0A0A0A]">Shared by:</strong> {snap.generated_by_name || '—'}</span>
          <span><Clock className="w-3 h-3 inline mr-1" /> Captured {snap.created_at ? format(new Date(snap.created_at), 'MMM d, yyyy HH:mm') : '—'} UTC</span>
          <span>Expires {snap.expires_at ? format(new Date(snap.expires_at), 'MMM d, yyyy') : '—'}</span>
          <span className="flex items-center gap-1"><Eye className="w-3 h-3" /> {snap.views || 0} view{snap.views === 1 ? '' : 's'}</span>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-6 py-6 space-y-6">
        {/* KPIs */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4" data-testid="snapshot-kpis">
          <Card className="border-[#E5E7EB]"><CardContent className="p-4 flex items-center gap-4">
            <div className="w-12 h-12 rounded-xl bg-[#002FA7]/10 flex items-center justify-center"><Users className="w-6 h-6 text-[#002FA7]" /></div>
            <div><p className="text-2xl font-bold text-[#0A0A0A]">{p.total_submissions || 0}</p><p className="text-xs text-[#9CA3AF]">Total Submissions</p></div>
          </CardContent></Card>
          <Card className="border-[#E5E7EB]"><CardContent className="p-4 flex items-center gap-4">
            <div className="w-12 h-12 rounded-xl bg-[#34C759]/10 flex items-center justify-center"><Target className="w-6 h-6 text-[#34C759]" /></div>
            <div><p className="text-2xl font-bold text-[#0A0A0A]">{p.avg_score || 0}%</p><p className="text-xs text-[#9CA3AF]">Average Score</p></div>
          </CardContent></Card>
          {Object.entries(sectionAverages).map(([name, val], i) => (
            <Card key={name} className="border-[#E5E7EB]"><CardContent className="p-4 flex items-center gap-4">
              <div className="w-12 h-12 rounded-xl flex items-center justify-center" style={{ backgroundColor: `${COLORS[i + 2]}15` }}>
                <BarChart3 className="w-6 h-6" style={{ color: COLORS[i + 2] }} /></div>
              <div><p className="text-2xl font-bold text-[#0A0A0A]">{val}%</p><p className="text-xs text-[#9CA3AF]">{name}</p></div>
            </CardContent></Card>
          ))}
        </div>

        {/* Radar + Maturity pie */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <Card className="border-[#E5E7EB]" data-testid="snapshot-radar">
            <CardHeader><CardTitle className="text-base">Section Performance</CardTitle></CardHeader>
            <CardContent>
              {radarData.length > 0 ? (
                <ResponsiveContainer width="100%" height={300}>
                  <RadarChart data={radarData}>
                    <PolarGrid stroke="#E5E7EB" />
                    <PolarAngleAxis dataKey="subject" tick={{ fontSize: 11, fill: '#4B5563' }} />
                    <PolarRadiusAxis angle={30} domain={[0, 100]} tick={{ fontSize: 10 }} />
                    <Radar name="Avg Score" dataKey="score" stroke="#002FA7" fill="#002FA7" fillOpacity={0.25} strokeWidth={2} />
                  </RadarChart>
                </ResponsiveContainer>
              ) : <div className="flex items-center justify-center h-[300px] text-[#9CA3AF] text-sm">No data</div>}
            </CardContent>
          </Card>
          <Card className="border-[#E5E7EB]" data-testid="snapshot-maturity">
            <CardHeader><CardTitle className="text-base">Maturity Distribution</CardTitle></CardHeader>
            <CardContent>
              {maturityData.length > 0 ? (
                <ResponsiveContainer width="100%" height={300}>
                  <PieChart>
                    <Pie data={maturityData} cx="50%" cy="50%" outerRadius={100} dataKey="value" label={({ name, value }) => `${name} (${value})`}>
                      {maturityData.map((entry) => <Cell key={entry.name} fill={MATURITY_COLORS[entry.name] || '#9CA3AF'} />)}
                    </Pie>
                    <Legend /><ReTooltip />
                  </PieChart>
                </ResponsiveContainer>
              ) : <div className="flex items-center justify-center h-[300px] text-[#9CA3AF] text-sm">No data</div>}
            </CardContent>
          </Card>
        </div>

        {/* Section bar */}
        <Card className="border-[#E5E7EB]" data-testid="snapshot-bar">
          <CardHeader><CardTitle className="text-base">Section Average Scores</CardTitle></CardHeader>
          <CardContent>
            {barData.length > 0 ? (
              <ResponsiveContainer width="100%" height={250}>
                <BarChart data={barData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
                  <XAxis dataKey="name" tick={{ fontSize: 11, fill: '#4B5563' }} />
                  <YAxis domain={[0, 100]} tick={{ fontSize: 11 }} />
                  <ReTooltip formatter={(value) => [`${value}%`, 'Score']} />
                  <Bar dataKey="score" radius={[6, 6, 0, 0]}>{barData.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}</Bar>
                </BarChart>
              </ResponsiveContainer>
            ) : <div className="flex items-center justify-center h-[250px] text-[#9CA3AF] text-sm">No data</div>}
          </CardContent>
        </Card>

        {/* Leaderboard */}
        <Card className="border-[#E5E7EB]" data-testid="snapshot-leaderboard">
          <CardHeader><CardTitle className="text-base flex items-center gap-2"><Award className="w-5 h-5 text-[#FF9500]" /> Leaderboard</CardTitle></CardHeader>
          <CardContent className="p-0">
            <div className="divide-y divide-[#E5E7EB]">
              {(p.leaderboard || []).map((entry, idx) => (
                <div key={idx} className="flex items-center gap-4 px-4 py-3">
                  <span className={`w-7 h-7 rounded-full flex items-center justify-center text-sm font-bold ${idx === 0 ? 'bg-[#FFD60A] text-[#0A0A0A]' : idx === 1 ? 'bg-[#C0C0C0] text-[#0A0A0A]' : idx === 2 ? 'bg-[#CD7F32] text-white' : 'bg-[#F3F4F6] text-[#4B5563]'}`}>{idx + 1}</span>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-[#0A0A0A]">{entry.user_name}</p>
                    {entry.user_email && <p className="text-[11px] text-[#6B7280] truncate">{entry.user_email}</p>}
                    <p className="text-[10px] text-[#9CA3AF]">{entry.created_at ? format(new Date(entry.created_at), 'MMM d, yyyy') : ''}</p>
                  </div>
                  <Badge className={`text-xs ${entry.maturity_level === 'Advanced' ? 'bg-[#34C759] text-white' : entry.maturity_level === 'Intermediate' ? 'bg-[#007AFF] text-white' : entry.maturity_level === 'Developing' ? 'bg-[#FF9500] text-white' : 'bg-[#FF3B30] text-white'}`}>{entry.maturity_level}</Badge>
                  <span className="text-lg font-bold text-[#002FA7]">{entry.overall_score}%</span>
                </div>
              ))}
              {(p.leaderboard || []).length === 0 && <div className="px-4 py-8 text-center text-[#9CA3AF] text-sm">No submissions yet</div>}
            </div>
          </CardContent>
        </Card>

        <p className="text-[10px] text-[#9CA3AF] text-center py-4">
          This is a tamper-evident read-only snapshot generated by KStreak Knowledge Management System.
          Numbers were frozen at capture time and will not change as new submissions arrive.
        </p>
      </div>
    </div>
  );
}
